import React from 'react';
import { User } from 'lucide-react';

export function RealVideoRoom({ session }) {
  return (
    <div className="card-theraflow overflow-hidden" data-testid="video-room-real">
      <div className="relative bg-[#1a1a1a] rounded-t-xl" style={{ aspectRatio: '16/9' }}>
        <iframe
          src={session.video_room_url}
          allow="camera; microphone; fullscreen; display-capture"
          className="w-full h-full absolute inset-0 rounded-t-xl"
          style={{ border: 'none', minHeight: '450px' }}
          title="Video Call"
        />
      </div>
      <div className="p-3 bg-[#1a1a1a] rounded-b-xl flex items-center justify-between">
        <span className="text-white/70 text-sm flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Chamada ao vivo
        </span>
        <a href={session.video_room_url} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white text-xs underline">
          Abrir em nova aba
        </a>
      </div>
    </div>
  );
}

export function MockVideoRoom({ session, onEnd }) {
  const videoRef = React.useRef(null);
  const [cameraOn, setCameraOn] = React.useState(true);
  const [micOn, setMicOn] = React.useState(true);
  const [stream, setStream] = React.useState(null);
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    let mediaStream;
    async function startCamera() {
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setStream(mediaStream);
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
      } catch (_) { /* camera unavailable */ }
    }
    startCamera();
    return () => { if (mediaStream) mediaStream.getTracks().forEach(t => t.stop()); };
  }, []);

  React.useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  const toggleCamera = () => {
    if (stream) { stream.getVideoTracks().forEach(t => { t.enabled = !t.enabled; }); setCameraOn(p => !p); }
  };
  const toggleMic = () => {
    if (stream) { stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; }); setMicOn(p => !p); }
  };
  const formatTime = (secs) => `${Math.floor(secs / 60).toString().padStart(2, '0')}:${(secs % 60).toString().padStart(2, '0')}`;

  const { Video, VideoOff, Mic, MicOff, PhoneOff } = require('lucide-react');

  return (
    <div className="card-theraflow overflow-hidden" data-testid="video-room">
      <div className="relative bg-[#1a1a1a] aspect-video rounded-t-xl">
        <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover ${!cameraOn ? 'hidden' : ''}`} />
        {!cameraOn && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-[#4A5D4E] flex items-center justify-center">
              <User className="w-12 h-12 text-white" strokeWidth={1.5} />
            </div>
          </div>
        )}
        <div className="absolute top-4 right-4 w-32 h-24 rounded-lg bg-[#2a2a2a] border border-white/10 flex items-center justify-center">
          <div className="text-center">
            <User className="w-8 h-8 text-white/50 mx-auto" strokeWidth={1.5} />
            <p className="text-xs text-white/50 mt-1">Aguardando...</p>
          </div>
        </div>
        <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> {formatTime(elapsed)}
        </div>
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#D4A373]/90 text-white text-xs font-medium">
          Preview - Video Call Mockado
        </div>
      </div>
      <div className="p-4 bg-[#1a1a1a] flex items-center justify-center gap-4 rounded-b-xl">
        <button onClick={toggleMic} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${micOn ? 'bg-[#333] hover:bg-[#444] text-white' : 'bg-red-500 text-white'}`} data-testid="toggle-mic">
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button onClick={toggleCamera} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${cameraOn ? 'bg-[#333] hover:bg-[#444] text-white' : 'bg-red-500 text-white'}`} data-testid="toggle-camera">
          {cameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        {onEnd && (
          <button onClick={onEnd} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors" data-testid="end-call">
            <PhoneOff className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
